const withoutKeys = (value, keys) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([key]) => !keys.has(key)),
  );

const COURSE_READ_ONLY_FIELDS = new Set([
  "id",
  "created_at",
  "updated_at",
  "lessons",
  "modules",
  "category",
  "instructor",
  "lessons_count",
  "enrollments",
  "total_students",
  "completion_rate",
  "total_duration",
  "averageRating",
  "totalRatings",
]);

const MODULE_READ_ONLY_FIELDS = new Set([
  "id",
  "course_id",
  "created_at",
  "updated_at",
]);

const LESSON_READ_ONLY_FIELDS = new Set([
  "id",
  "course_id",
  "module_id",
  "created_at",
  "updated_at",
]);

const DELIVERY_MODE_READ_ONLY_FIELDS = new Set([
  "course_id",
  "created_at",
  "updated_at",
]);

const LIVE_PAIR_READ_ONLY_FIELDS = new Set([
  "id",
  "course_id",
  "created_at",
  "updated_at",
  "pdf_lesson_id",
  "quiz_lesson_id",
]);

const MATERIAL_READ_ONLY_FIELDS = new Set([
  "id",
  "course_id",
  "created_at",
  "updated_at",
]);

const RESOURCE_READ_ONLY_FIELDS = new Set([
  "id",
  "course_id",
  "lesson_id",
  "created_at",
  "download_count",
]);

export const duplicateCourseWithContent = async (courseId, operations) => {
  let newCourse = null;
  try {
    const original = await operations.loadCourse(courseId);
    const coursePayload = {
      ...withoutKeys(original, COURSE_READ_ONLY_FIELDS),
      title: `Copy of ${original.title}`,
      status: "draft",
      is_active: false,
    };
    newCourse = await operations.createCourse(coursePayload);

    const modules = await operations.loadModules(courseId);
    const moduleIds = new Map();
    for (const module of modules || []) {
      const created = await operations.createModule({
        ...withoutKeys(module, MODULE_READ_ONLY_FIELDS),
        course_id: newCourse.id,
      });
      moduleIds.set(module.id, created.id);
    }

    const lessons = await operations.loadLessons(courseId);
    const lessonIds = new Map();
    for (const lesson of lessons || []) {
      const mappedModuleId = lesson.module_id
        ? moduleIds.get(lesson.module_id)
        : null;
      if (lesson.module_id && !mappedModuleId) {
        throw new Error("A lesson module could not be copied");
      }
      const created = await operations.createLesson({
        ...withoutKeys(lesson, LESSON_READ_ONLY_FIELDS),
        course_id: newCourse.id,
        module_id: mappedModuleId,
      });
      lessonIds.set(lesson.id, created.id);
    }

    if (operations.loadDeliveryModes && operations.createDeliveryMode) {
      const deliveryModes = await operations.loadDeliveryModes(courseId);
      for (const mode of deliveryModes || []) {
        await operations.createDeliveryMode({
          ...withoutKeys(mode, DELIVERY_MODE_READ_ONLY_FIELDS),
          course_id: newCourse.id,
        });
      }
    }

    if (operations.loadLivePairs && operations.createLivePair) {
      const livePairs = await operations.loadLivePairs(courseId);
      for (const pair of livePairs || []) {
        const pdfLessonId = lessonIds.get(pair.pdf_lesson_id);
        const quizLessonId = lessonIds.get(pair.quiz_lesson_id);
        if (!pdfLessonId || !quizLessonId) {
          throw new Error("A live-content lesson pair could not be copied");
        }
        await operations.createLivePair({
          ...withoutKeys(pair, LIVE_PAIR_READ_ONLY_FIELDS),
          course_id: newCourse.id,
          pdf_lesson_id: pdfLessonId,
          quiz_lesson_id: quizLessonId,
        });
      }
    }

    if (
      operations.loadInstructorMaterials &&
      operations.createInstructorMaterial
    ) {
      const materials = await operations.loadInstructorMaterials(courseId);
      for (const material of materials || []) {
        await operations.createInstructorMaterial({
          ...withoutKeys(material, MATERIAL_READ_ONLY_FIELDS),
          course_id: newCourse.id,
        });
      }
    }

    if (operations.loadResources && operations.createResource) {
      const resources = await operations.loadResources(courseId);
      for (const resource of resources || []) {
        const lessonId = resource.lesson_id
          ? lessonIds.get(resource.lesson_id)
          : null;
        if (resource.lesson_id && !lessonId) {
          throw new Error("A lesson resource could not be copied");
        }
        await operations.createResource({
          ...withoutKeys(resource, RESOURCE_READ_ONLY_FIELDS),
          course_id: newCourse.id,
          lesson_id: lessonId,
        });
      }
    }

    return newCourse;
  } catch (error) {
    if (newCourse?.id) {
      await operations.cleanupCourse(newCourse.id).catch(() => {});
    }
    throw error;
  }
};
